import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import SearchableSelect from '../../../components/ui/SearchableSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { FileText, FileSpreadsheet, Download, Loader2, AlertCircle } from 'lucide-react';
import { periodKeyLabel } from '../uploadCycle';
import { prettyTemplateName, type UseDataUploadResult } from './useDataUpload';

type UploadTargetCardProps = Pick<
  UseDataUploadResult,
  | 'templates' | 'templatesLoading' | 'templatesError'
  | 'selectedTemplate' | 'setSelectedTemplate' | 'selectedTemplateData'
  | 'periods' | 'period' | 'setPeriod'
  | 'suppliers' | 'showSupplierSelect' | 'supplierId' | 'setSupplierId'
  | 'handleDownloadTemplate'
>;

/** Which template, which month, and (for customers) which supplier the file is for. */
export default function UploadTargetCard(props: UploadTargetCardProps) {
  const {
    templates, templatesLoading, templatesError,
    selectedTemplate, setSelectedTemplate, selectedTemplateData,
    periods, period, setPeriod,
    suppliers, showSupplierSelect, supplierId, setSupplierId,
    handleDownloadTemplate,
  } = props;

  // The API list may not include the client-side fallback period; keep it selectable.
  const periodOptions = periods.some((p) => p.period === period) || !period
    ? periods
    : [{ period, label: periodKeyLabel(period), due_date: '', is_current: false }, ...periods];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Select Template
        </CardTitle>
        <CardDescription>Choose the data template that matches your file format</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {templatesError ? (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {templatesError}
          </div>
        ) : (
          <Select value={selectedTemplate} onValueChange={setSelectedTemplate} disabled={templatesLoading}>
            <SelectTrigger data-testid="template-select">
              {templatesLoading ? (
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
                </span>
              ) : (
                <SelectValue placeholder="Select a template..." />
              )}
            </SelectTrigger>
            <SelectContent>
              {templates.map((template) => (
                <SelectItem key={template.id} value={template.id.toString()}>
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    <span>{prettyTemplateName(template.name)}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {selectedTemplateData && (
          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">{selectedTemplateData.description}</p>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Expected Columns:</p>
              <div className="flex flex-wrap gap-1">
                {selectedTemplateData.columns.slice(0, 8).map((col) => (
                  <Badge
                    key={col}
                    variant="secondary"
                    className="text-xs"
                    title={selectedTemplateData.required_columns?.includes(col) ? 'Required' : undefined}
                  >
                    {col}{selectedTemplateData.required_columns?.includes(col) ? ' *' : ''}
                  </Badge>
                ))}
                {selectedTemplateData.columns.length > 8 && (
                  <Badge variant="outline" className="text-xs">
                    +{selectedTemplateData.columns.length - 8} more
                  </Badge>
                )}
              </div>
              {selectedTemplateData.required_columns?.length > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">* required</p>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="upload-period">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger id="upload-period" data-testid="upload-period-select">
                <SelectValue placeholder="Select a month" />
              </SelectTrigger>
              <SelectContent>
                {periodOptions.map((p) => (
                  <SelectItem key={p.period} value={p.period}>
                    {p.label}{p.is_current ? ' (current)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">The month this file reports on.</p>
          </div>

          {showSupplierSelect && (
            <div className="space-y-1.5">
              <Label htmlFor="upload-supplier">Uploading on behalf of</Label>
              <div id="upload-supplier" data-testid="upload-supplier-select">
                <SearchableSelect
                  options={suppliers.map((s) => ({ value: String(s.id), label: s.name }))}
                  value={supplierId}
                  onChange={setSupplierId}
                  placeholder="No supplier — my own data"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Marks that supplier's month as uploaded (Supplier Performance and Quality files).
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
